// cp_anon_id persistence (spec §5.1) — the FIRST persistent identifier this
// SDK writes to client storage. persistence:'memory' is a genuine
// no-storage path for consent-restrictive customers (degrades unique-user
// accuracy to per-pageload; documented in the changelog at release), and
// every localStorage access is try/catch-guarded — Safari private mode and
// storage-blocked iframes throw on ACCESS, not just on write.
export const ANON_ID_STORAGE_KEY = 'cp_anon_id';

export function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Math.random fallback for pre-2021 browsers without crypto.randomUUID.
  let out = '';
  const hex = '0123456789abcdef';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += '-';
    } else if (i === 14) {
      out += '4';
    } else {
      const r = Math.floor(Math.random() * 16);
      out += hex[i === 19 ? (r & 0x3) | 0x8 : r];
    }
  }
  return out;
}

let memoryAnonId: string | null = null;

export function resolveAnonId(persistence: 'localStorage' | 'memory'): string {
  if (persistence === 'localStorage') {
    let readable = true;
    let existing: string | null = null;
    try {
      existing = window.localStorage.getItem(ANON_ID_STORAGE_KEY);
    } catch {
      // Storage blocked on read — degrade to memory mode for this page load.
      readable = false;
    }
    if (existing && existing.length > 0 && existing.length <= 255) return existing;
    if (readable) {
      // Generate only AFTER the read succeeded, and never discard the result:
      // if the write throws (quota, private mode), the same id becomes the
      // in-memory fallback instead of minting a second one.
      const fresh = memoryAnonId ?? generateUuid();
      try {
        window.localStorage.setItem(ANON_ID_STORAGE_KEY, fresh);
        return fresh;
      } catch {
        memoryAnonId = fresh;
        return fresh;
      }
    }
  }
  if (!memoryAnonId) memoryAnonId = generateUuid();
  return memoryAnonId;
}

/** Test hook only. */
export function _resetAnonIdForTests(): void {
  memoryAnonId = null;
}
